import { getTableColumns } from "drizzle-orm";
import { boolean, index, pgTable, text, uuid } from "drizzle-orm/pg-core";
import {
  createInsertSchema,
  createSelectSchema,
  createUpdateSchema,
} from "drizzle-zod";
import z from "zod";

import { householdMemberSchema } from "./householdMembersSchema";
import { customTimestamp } from "./utils/types";

export const applicationLastStepEnumSchema = z.enum([
  "BROKER_CONSENT",
  "CITIZENSHIP_STATUS",
  "COVERAGE_STATUS",
  "DATE_OF_BIRTH",
  "ADDRESS",
  "MARITAL_STATUS",
  "CHILD_CAREGIVER_STATUS",
  "OTHER_STATUSES",
  "TAX_INFORMATION",
  "WORK_AND_INCOME",
  "HOUSEHOLD_EXPENSES",
  "DOCUMENTS",
]);

export type ApplicationLastStepEnumType = z.infer<
  typeof applicationLastStepEnumSchema
>;

export const preferredLanguageEnumSchema = z.enum([
  "en",
  "es",
  "zh",
  "vi",
  "ru",
  "ar",
  "tl",
  "ht",
]);

export type PreferredLanguageEnumType = z.infer<
  typeof preferredLanguageEnumSchema
>;

// Stored normalized so the unique index is case-insensitive.
export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(32)
  .regex(/^[a-z0-9_-]+$/);

export const patientsTable = pgTable(
  "patients",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    email: text("email").unique(),
    username: text("username").unique(),
    hasCompletedProfile: boolean("has_completed_profile")
      .notNull()
      .default(false),
    hasSignedConsentDocument: boolean("has_signed_consent_document")
      .notNull()
      .default(false),
    applicationLastStep: text(
      "application_last_step",
    ).$type<ApplicationLastStepEnumType>(),
    memberId: text("member_id"),
    preferredLanguage:
      text("preferred_language").$type<PreferredLanguageEnumType>(),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("patients_email_idx").on(table.email),
    index("patients_member_id_idx").on(table.memberId),
  ],
);

export const patientsTableColumns = getTableColumns(patientsTable);

export const patientSchema = createSelectSchema(patientsTable, {
  applicationLastStep: applicationLastStepEnumSchema.nullish(),
  preferredLanguage: preferredLanguageEnumSchema.nullish(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PatientType = z.infer<typeof patientSchema>;

export const patientWithHouseholdMemberSchema = patientSchema.extend({
  householdMember: householdMemberSchema,
});

export type PatientWithHouseholdMemberType = z.infer<
  typeof patientWithHouseholdMemberSchema
>;

export const insertPatientSchema = createInsertSchema(patientsTable, {
  email: z.email().nullish(),
  username: usernameSchema.nullish(),
  applicationLastStep: applicationLastStepEnumSchema.optional(),
  preferredLanguage: preferredLanguageEnumSchema.nullish(),
}).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertPatientType = z.infer<typeof insertPatientSchema>;

export const updatePatientSchema = createUpdateSchema(patientsTable, {
  applicationLastStep: applicationLastStepEnumSchema.nullish(),
  preferredLanguage: preferredLanguageEnumSchema.nullish(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial();

export type UpdatePatientType = z.infer<typeof updatePatientSchema>;
